import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Code,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { CheckIcon, CopyIcon, ExternalLinkIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";

const WORKER_CONFIG_DOC = doc(db, "systemConfig", "cloudMevWorker");

function generateWorkerSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function withHttps(value: string): string {
  let next = String(value || "").trim();
  if (!next) return "";
  if (!/^https?:\/\//i.test(next)) next = `https://${next}`;
  if (next.toLowerCase().startsWith("http://")) next = `https://${next.slice(7)}`;
  return next.replace(/\/+$/, "");
}

function isHttpsWorkerUrl(value: string): boolean {
  try {
    const url = new URL(withHttps(value));
    return url.protocol === "https:" && Boolean(url.hostname);
  } catch {
    return false;
  }
}

const CloudMevWorkerSettings: React.FC = () => {
  const toast = useToast();
  const [workerUrl, setWorkerUrl] = useState("");
  const [workerSecret, setWorkerSecret] = useState("");
  const [hasSecret, setHasSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const infoBgColor = useColorModeValue("blue.50", "blue.900");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(WORKER_CONFIG_DOC);
        if (snap.exists()) {
          const data = snap.data() || {};
          setWorkerUrl(typeof data.workerUrl === "string" ? data.workerUrl : "");
          setHasSecret(Boolean(data.workerSecret));
        }
      } catch (error) {
        console.error("Error loading Cloud MEV worker settings:", error);
        toast({
          title: "Error",
          description: "Failed to load Worker settings",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  const handleGenerate = () => {
    setWorkerSecret(generateWorkerSecret());
    setShowSecret(true);
  };

  const handleCopy = async () => {
    if (!workerSecret) return;
    await navigator.clipboard.writeText(workerSecret);
    setCopied(true);
    toast({
      title: "Copied",
      description: "Paste this exact value into the Worker as INTERNAL_SHARED_SECRET.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    const normalizedUrl = withHttps(workerUrl);
    setWorkerUrl(normalizedUrl);
    if (!isHttpsWorkerUrl(normalizedUrl)) {
      toast({
        title: "Validation error",
        description: "Enter the Worker URL, for example mev-csr.your-subdomain.workers.dev",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!workerSecret && !hasSecret) {
      toast({
        title: "Validation error",
        description: "Generate a Worker secret first, then paste the same value into Cloudflare.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const existing = await getDoc(WORKER_CONFIG_DOC);
      const previous = existing.exists() ? existing.data() || {} : {};
      const secretToSave = workerSecret || previous.workerSecret;
      if (!secretToSave) {
        throw new Error("Generate a Worker secret first");
      }
      const wrapKey =
        typeof previous.wrapKey === "string" && previous.wrapKey.length === 64
          ? previous.wrapKey
          : generateWorkerSecret();

      await setDoc(
        WORKER_CONFIG_DOC,
        {
          workerUrl: normalizedUrl,
          workerSecret: secretToSave,
          wrapKey,
          configured: true,
          updatedAt: serverTimestamp(),
          updatedBy: "super_admin",
        },
        { merge: true }
      );
      setWorkerUrl(normalizedUrl);
      setHasSecret(true);
      toast({
        title: "Saved",
        description: "Worker URL and secret saved.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving Cloud MEV worker settings:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to save Worker settings",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">How to connect the Worker</AlertTitle>
            <AlertDescription fontSize="xs">
              1. Open the <Code>mev-csr</Code> Worker. On the right, under Domains and routes,
              enable <Code>workers.dev</Code> (yours is currently disabled, so there is no URL).
              <br />
              2. After it is enabled, copy the URL (example{" "}
              <Code>mev-csr.your-subdomain.workers.dev</Code>). You can paste it with or without
              https:// — we add https:// when you save.
              <br />
              3. Click <strong>Generate secret</strong>, then in the Worker go to Settings →
              Variables and Secrets and add <Code>INTERNAL_SHARED_SECRET</Code> with the same
              value.
              <br />
              4. Click <strong>Save</strong> here.
            </AlertDescription>
          </Box>
        </Alert>

        <Box bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Worker URL</FormLabel>
              <Input
                type="text"
                value={workerUrl}
                onChange={(event) => setWorkerUrl(event.target.value)}
                placeholder="mev-csr.your-subdomain.workers.dev"
              />
              <FormHelperText fontSize="xs">
                Paste the workers.dev URL. https:// is added automatically.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Worker shared secret</FormLabel>
              <InputGroup>
                <Input
                  type={showSecret ? "text" : "password"}
                  value={workerSecret}
                  readOnly
                  placeholder={
                    hasSecret
                      ? "A secret is already saved. Generate only if you are rotating it."
                      : "Generate a secret, then paste it into Cloudflare"
                  }
                />
                <InputRightElement width="4.5rem">
                  <HStack spacing={0}>
                    <IconButton
                      aria-label={showSecret ? "Hide secret" : "Show secret"}
                      icon={showSecret ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      variant="ghost"
                      isDisabled={!workerSecret}
                      onClick={() => setShowSecret((value) => !value)}
                    />
                    <IconButton
                      aria-label="Copy secret"
                      icon={copied ? <CheckIcon color="green.500" /> : <CopyIcon />}
                      size="sm"
                      variant="ghost"
                      isDisabled={!workerSecret}
                      onClick={handleCopy}
                    />
                  </HStack>
                </InputRightElement>
              </InputGroup>
              <FormHelperText fontSize="xs">
                This is not your Cloudflare login, API token, Account ID, or Zone ID. It is a
                random string that Functions send as <Code>x-mev-internal</Code> so only our
                backend can call the Worker.
              </FormHelperText>
            </FormControl>

            <HStack>
              <Button variant="outline" onClick={handleGenerate}>
                Generate secret
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={saving}
                loadingText="Saving..."
              >
                Save
              </Button>
            </HStack>
          </VStack>
        </Box>

        <Box bg={infoBgColor} borderRadius="lg" p={4}>
          <Text fontSize="md" fontWeight="semibold" mb={3}>
            Quick links
          </Text>
          <VStack align="stretch" spacing={2}>
            <Link href="https://dash.cloudflare.com" isExternal color="blue.600">
              Cloudflare Dashboard <ExternalLinkIcon mx={1} />
            </Link>
            <Link href="https://developers.cloudflare.com/workers/" isExternal color="blue.600">
              Cloudflare Workers documentation <ExternalLinkIcon mx={1} />
            </Link>
          </VStack>
        </Box>

        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Important</AlertTitle>
            <AlertDescription fontSize="xs">
              After you generate a new secret, Cloudflare and this page must have the same value
              or enrollment will return unauthorized.
              <br />
              Store Cloud MEV screens only enroll a store certificate. They cannot open or
              edit Worker credentials.
            </AlertDescription>
          </Box>
        </Alert>
    </VStack>
  );
};

export default CloudMevWorkerSettings;
